import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  AlertTriangle, AlertCircle, Clock, Package, TrendingDown, 
  ShoppingCart, ChevronRight, RefreshCw, CheckCircle2, XCircle,
  Calendar, Zap, ArrowRight, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/format';

const API = process.env.REACT_APP_BACKEND_URL;

// Severity badge colors
const SEVERITY_STYLES = {
  CRITICAL: 'bg-rose-50 text-rose-700 border-rose-200',
  HIGH: 'bg-amber-50 text-amber-700 border-amber-200',
  MEDIUM: 'bg-slate-50 text-slate-700 border-slate-200',
  LOW: 'bg-slate-50 text-slate-600 border-slate-200',
  WATCH: 'bg-slate-50 text-slate-500 border-slate-200',
  OK: 'bg-emerald-50 text-emerald-700 border-emerald-200',
};

const SEVERITY_ICONS = {
  CRITICAL: <AlertTriangle className="w-4 h-4" />,
  HIGH: <AlertCircle className="w-4 h-4" />,
  MEDIUM: <Clock className="w-4 h-4" />,
  LOW: <Package className="w-4 h-4" />,
};

export default function MaterialGaps() {
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');
  const [loading, setLoading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  
  // Data
  const [gaps, setGaps] = useState([]);
  const [summary, setSummary] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  
  // View mode
  const [viewMode, setViewMode] = useState('list'); // list, timeline, dashboard
  const [selectedGaps, setSelectedGaps] = useState([]);
  
  // Filters
  const [severityFilter, setSeverityFilter] = useState('');
  const [horizonWeeks, setHorizonWeeks] = useState(8);

  // Fetch projects
  useEffect(() => {
    fetch(`${API}/api/projects`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        const active = (Array.isArray(data) ? data : []).filter(p => p.status !== 'Completed');
        setProjects(active);
        if (active.length > 0) setSelectedProject(active[0].id);
      })
      .catch(console.error);
  }, []);

  // Fetch material gaps
  const fetchGaps = useCallback(async () => {
    if (!selectedProject && viewMode !== 'dashboard') return;
    
    setLoading(true);
    try {
      if (viewMode === 'dashboard') {
        const res = await fetch(`${API}/api/material-gaps/dashboard`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setDashboard(data);
        }
      } else if (viewMode === 'timeline') {
        const res = await fetch(
          `${API}/api/material-gaps/timeline?project_id=${selectedProject}`,
          { credentials: 'include' }
        );
        if (res.ok) {
          const data = await res.json();
          setTimeline(data.timeline || []);
          setSummary(data.summary);
        }
      } else {
        const params = new URLSearchParams({
          project_id: selectedProject,
          horizon_weeks: horizonWeeks.toString(),
          include_watch: 'false',
        });
        const res = await fetch(`${API}/api/material-gaps?${params}`, { credentials: 'include' });
        if (res.ok) {
          const data = await res.json();
          setGaps(data.gaps || []);
          setSummary(data.summary);
        }
      }
    } catch (err) {
      console.error('Error fetching gaps:', err);
      toast.error('Failed to load material gaps');
    } finally {
      setLoading(false);
    }
  }, [selectedProject, viewMode, horizonWeeks]);

  useEffect(() => {
    fetchGaps();
  }, [fetchGaps]);

  // Create PR from gap
  const handleCreatePR = async (boqId) => {
    try {
      const res = await fetch(`${API}/api/material-gaps/${boqId}/create-pr`, {
        method: 'POST',
        credentials: 'include',
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`PR ${data.pr_no} created!`);
        fetchGaps();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create PR');
      }
    } catch (err) {
      toast.error('Error creating PR');
    }
  };

  // Bulk create PRs
  const handleBulkCreatePR = async () => {
    if (selectedGaps.length === 0) {
      toast.error('Select items first');
      return;
    }
    
    try {
      const res = await fetch(`${API}/api/material-gaps/bulk-create-pr`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(selectedGaps),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Created ${data.prs_created} PRs for ${data.total_items} items`);
        setSelectedGaps([]);
        fetchGaps();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to create PRs');
      }
    } catch (err) {
      toast.error('Error creating PRs');
    }
  };

  // Toggle selection
  const toggleSelection = (boqId) => {
    setSelectedGaps(prev => 
      prev.includes(boqId) ? prev.filter(id => id !== boqId) : [...prev, boqId]
    );
  };

  // Select all critical/high
  const selectAllUrgent = () => {
    const urgentIds = gaps
      .filter(g => g.severity === 'CRITICAL' || g.severity === 'HIGH')
      .map(g => g.boq_id);
    setSelectedGaps(urgentIds);
  };

  // Filter gaps
  const filteredGaps = gaps.filter(g => {
    if (severityFilter && g.severity !== severityFilter) return false;
    return true;
  });

  const formatCurrency = (val) => `₹${(val || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  return (
    <div className="space-y-6" data-testid="material-gaps-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <TrendingDown className="w-6 h-6 text-[#3b4872]" />
            Material Gaps
          </h1>
          <p className="text-sm text-gray-500">
            Early warning system - Detect shortages WEEKS before they impact site
          </p>
        </div>
        <div className="flex items-center gap-3">
          {viewMode !== 'dashboard' && (
            <select
              data-testid="material-gaps-project"
              value={selectedProject}
              onChange={(e) => setSelectedProject(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
            >
              <option value="">Select Project</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={fetchGaps}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* View Mode Tabs */}
      <div className="flex gap-2 border-b border-gray-200 pb-2">
        {[
          { id: 'list', label: 'Gap List', icon: AlertTriangle },
          { id: 'timeline', label: 'Timeline', icon: Calendar },
          { id: 'dashboard', label: 'Executive Dashboard', icon: Zap },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setViewMode(tab.id)}
            data-testid={`material-gaps-tab-${tab.id}`}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
              viewMode === tab.id
                ? 'bg-slate-50 text-[#3b4872] border-b-2 border-[#3b4872]'
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Executive Dashboard View */}
      {viewMode === 'dashboard' && dashboard && (
        <div className="space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            <Card className="border-rose-200 bg-rose-50/40">
              <CardContent className="pt-4">
                <div className="text-[10px] font-display uppercase tracking-[0.16em] text-rose-700 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" /> Critical
                </div>
                <div className="text-2xl font-display font-extrabold mt-1.5 tracking-tight text-rose-700">
                  {dashboard.summary?.critical_count || 0}
                </div>
                <div className="text-xs text-rose-700/80 mt-0.5">Immediate action</div>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/40">
              <CardContent className="pt-4">
                <div className="text-[10px] font-display uppercase tracking-[0.16em] text-amber-700 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> High
                </div>
                <div className="text-2xl font-display font-extrabold mt-1.5 tracking-tight text-amber-700">
                  {dashboard.summary?.high_count || 0}
                </div>
                <div className="text-xs text-amber-700/80 mt-0.5">Within 2 weeks</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500 flex items-center gap-1">
                  <Clock className="w-3 h-3" /> Medium
                </div>
                <div className="text-2xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">
                  {dashboard.summary?.medium_count || 0}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Within 4 weeks</div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-4">
                <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Total Gaps</div>
                <div className="text-2xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">
                  {dashboard.summary?.total_gaps || 0}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">{dashboard.summary?.projects_with_gaps} projects</div>
              </CardContent>
            </Card>
            <Card className="col-span-2">
              <CardContent className="pt-4">
                <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500">Total Gap Value</div>
                <div className="text-2xl font-display font-extrabold mt-1.5 tracking-tight text-slate-900">
                  {formatCurrency(dashboard.summary?.total_gap_value)}
                </div>
                <div className="text-xs text-slate-500 mt-0.5">Materials to order urgently</div>
              </CardContent>
            </Card>
          </div>

          {/* Top Critical Items */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base flex items-center gap-2 text-slate-800">
                <AlertTriangle className="w-5 h-5 text-rose-600" />
                Top 10 Critical Shortages
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 border-y border-slate-200">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Project</th>
                      <th className="text-left px-4 py-2 font-medium text-slate-600">Item</th>
                      <th className="text-right px-4 py-2 font-medium text-slate-600">Gap Qty</th>
                      <th className="text-right px-4 py-2 font-medium text-slate-600">Gap Value</th>
                      <th className="text-center px-4 py-2 font-medium text-slate-600">Impact</th>
                      <th className="text-center px-4 py-2 font-medium text-slate-600">Severity</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(dashboard.top_critical_items || []).map((gap, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-2 font-medium">{gap.project_name}</td>
                        <td className="px-4 py-2">
                          <div className="font-mono text-xs text-gray-500">{gap.item_code}</div>
                          <div className="truncate max-w-xs">{gap.description}</div>
                        </td>
                        <td className="px-4 py-2 text-right font-medium">
                          {gap.gap_qty?.toLocaleString('en-IN')} {gap.unit}
                        </td>
                        <td className="px-4 py-2 text-right font-medium text-slate-800">
                          {formatCurrency(gap.gap_value)}
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`text-xs ${gap.days_until_impact <= 0 ? 'text-rose-700 font-bold' : ''}`}>
                            {gap.days_until_impact <= 0 ? 'NOW!' : `${gap.days_until_impact}d`}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-center">
                          <span className={`px-2 py-1 rounded text-xs font-medium border ${SEVERITY_STYLES[gap.severity]}`}>
                            {gap.severity}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Projects with Gaps */}
          <Card>
            <CardHeader className="py-3">
              <CardTitle className="text-base">Projects with Material Gaps</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-y">
                    <tr>
                      <th className="text-left px-4 py-2 font-medium text-gray-600">Project</th>
                      <th className="text-center px-4 py-2 font-medium text-rose-700">Critical</th>
                      <th className="text-center px-4 py-2 font-medium text-amber-700">High</th>
                      <th className="text-center px-4 py-2 font-medium text-slate-600">Medium</th>
                      <th className="text-right px-4 py-2 font-medium text-gray-600">Total Gaps</th>
                      <th className="text-right px-4 py-2 font-medium text-gray-600">Gap Value</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {(dashboard.projects_with_gaps || []).map((proj, idx) => (
                      <tr 
                        key={idx} 
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          setSelectedProject(proj.project_id);
                          setViewMode('list');
                        }}
                      >
                        <td className="px-4 py-2 font-medium">{proj.project_name}</td>
                        <td className="px-4 py-2 text-center">
                          {proj.critical > 0 && (
                            <span className="bg-rose-50 text-rose-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              {proj.critical}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {proj.high > 0 && (
                            <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              {proj.high}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {proj.medium > 0 && (
                            <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded-full text-xs font-bold">
                              {proj.medium}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-right">{proj.total_gaps}</td>
                        <td className="px-4 py-2 text-right font-medium">{formatCurrency(proj.gap_value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Timeline View */}
      {viewMode === 'timeline' && (
        <div className="space-y-4">
          {timeline.map((week, idx) => (
            <Card 
              key={idx}
              className={`${
                week.severity === 'CRITICAL' ? 'border-rose-200 bg-rose-50/40' :
                week.severity === 'HIGH' ? 'border-amber-200 bg-amber-50/40' :
                week.severity === 'MEDIUM' ? 'border-slate-200 bg-slate-50/40' : ''
              }`}
            >
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {week.severity !== 'OK' && SEVERITY_ICONS[week.severity]}
                    <span className="font-bold">{week.week_label}</span>
                    <span className="text-gray-500 font-normal">
                      ({week.start_date} - {week.end_date})
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-sm">
                      {week.items_count} items
                    </span>
                    <span className="font-bold text-slate-800">
                      {formatCurrency(week.total_value)}
                    </span>
                  </div>
                </CardTitle>
              </CardHeader>
              {week.items_count > 0 && (
                <CardContent className="pt-0">
                  <div className="space-y-2">
                    {week.items.map((item, i) => (
                      <div key={i} className="flex items-center justify-between text-sm bg-white rounded px-3 py-2">
                        <div className="flex-1">
                          <span className="font-mono text-xs text-gray-500 mr-2">{item.item_code}</span>
                          <span>{item.description}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="font-medium">{item.gap_qty} {item.unit}</span>
                          <span className="text-red-600 font-medium">{formatCurrency(item.gap_value)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              )}
            </Card>
          ))}
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Summary Cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <Card className={summary.critical_count > 0 ? 'border-rose-200 bg-rose-50/40' : ''}>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-display font-extrabold tracking-tight text-rose-700">{summary.critical_count}</div>
                  <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500 mt-0.5">Critical</div>
                </CardContent>
              </Card>
              <Card className={summary.high_count > 0 ? 'border-amber-200 bg-amber-50/40' : ''}>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-display font-extrabold tracking-tight text-amber-700">{summary.high_count}</div>
                  <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500 mt-0.5">High</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-display font-extrabold tracking-tight text-slate-900">{summary.medium_count}</div>
                  <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500 mt-0.5">Medium</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-2xl font-display font-extrabold tracking-tight text-slate-900">{summary.low_count}</div>
                  <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500 mt-0.5">Low</div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4 text-center">
                  <div className="text-xl font-display font-extrabold tracking-tight text-slate-900">{formatCurrency(summary.total_gap_value)}</div>
                  <div className="text-[10px] font-display uppercase tracking-[0.16em] text-slate-500 mt-0.5">Total Gap Value</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Actions Bar */}
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="flex flex-wrap gap-3">
              <select
                value={severityFilter}
                onChange={(e) => setSeverityFilter(e.target.value)}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="">All Severity</option>
                <option value="CRITICAL">Critical Only</option>
                <option value="HIGH">High Only</option>
                <option value="MEDIUM">Medium Only</option>
                <option value="LOW">Low Only</option>
              </select>
              <select
                value={horizonWeeks}
                onChange={(e) => setHorizonWeeks(parseInt(e.target.value))}
                className="border rounded-lg px-3 py-2 text-sm"
              >
                <option value="4">4 Weeks Horizon</option>
                <option value="8">8 Weeks Horizon</option>
                <option value="12">12 Weeks Horizon</option>
              </select>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={selectAllUrgent}>
                Select All Urgent
              </Button>
              <Button 
                size="sm" 
                onClick={handleBulkCreatePR}
                disabled={selectedGaps.length === 0}
                className="bg-[#3b4872] hover:bg-[#2d3856] text-white"
                data-testid="bulk-create-pr-btn"
              >
                <ShoppingCart className="w-4 h-4 mr-2" />
                Create PRs ({selectedGaps.length})
              </Button>
            </div>
          </div>

          {/* Gaps Table */}
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="w-10 px-3 py-3"></th>
                      <th className="text-left px-3 py-3 font-medium text-gray-600">Item</th>
                      <th className="text-center px-3 py-3 font-medium text-gray-600">Severity</th>
                      <th className="text-right px-3 py-3 font-medium text-gray-600">Available</th>
                      <th className="text-right px-3 py-3 font-medium text-gray-600">Required</th>
                      <th className="text-right px-3 py-3 font-medium text-gray-600">Gap</th>
                      <th className="text-right px-3 py-3 font-medium text-gray-600">Gap Value</th>
                      <th className="text-center px-3 py-3 font-medium text-gray-600">Impact In</th>
                      <th className="text-center px-3 py-3 font-medium text-gray-600">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {loading ? (
                      <tr><td colSpan={9} className="text-center py-8 text-gray-500">Analyzing gaps...</td></tr>
                    ) : filteredGaps.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="text-center py-8">
                          <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-600 mb-2" />
                          <p className="text-gray-500">No material gaps detected!</p>
                          <p className="text-sm text-gray-400">All materials are sufficient for upcoming work</p>
                        </td>
                      </tr>
                    ) : filteredGaps.map(gap => (
                      <tr 
                        key={gap.boq_id} 
                        data-testid={`gap-row-${gap.boq_id}`}
                        className={`hover:bg-gray-50 ${
                          gap.severity === 'CRITICAL' ? 'bg-rose-50/40' :
                          gap.severity === 'HIGH' ? 'bg-amber-50/40' : ''
                        }`}
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={selectedGaps.includes(gap.boq_id)}
                            onChange={() => toggleSelection(gap.boq_id)}
                            className="rounded"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div className="font-mono text-xs text-gray-500">{gap.item_code}</div>
                          <div className="text-sm truncate max-w-xs" title={gap.description}>
                            {gap.description}
                          </div>
                          <div className="text-xs text-gray-400">{gap.section}</div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium border ${SEVERITY_STYLES[gap.severity]}`}>
                            {SEVERITY_ICONS[gap.severity]}
                            {gap.severity}
                          </span>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="font-medium">{gap.available_qty?.toLocaleString('en-IN')}</div>
                          <div className="text-xs text-gray-500">{gap.unit}</div>
                          {gap.in_pipeline > 0 && (
                            <div className="text-xs text-[#3b4872]">+{gap.in_pipeline} in transit</div>
                          )}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="font-medium">{gap.required_qty?.toLocaleString('en-IN')}</div>
                          <div className="text-xs text-gray-500">{gap.tasks_count} tasks</div>
                        </td>
                        <td className="px-3 py-3 text-right">
                          <div className="font-bold text-rose-700">
                            {gap.gap_qty?.toLocaleString('en-IN')}
                          </div>
                          <div className="text-xs text-gray-500">{gap.unit}</div>
                        </td>
                        <td className="px-3 py-3 text-right font-medium text-slate-800">
                          {formatCurrency(gap.gap_value)}
                        </td>
                        <td className="px-3 py-3 text-center">
                          <div className={`font-bold ${
                            gap.days_until_impact <= 0 ? 'text-rose-700' :
                            gap.days_until_impact <= 7 ? 'text-amber-700' :
                            'text-gray-600'
                          }`}>
                            {gap.days_until_impact <= 0 ? 'NOW!' : 
                             gap.days_until_impact <= 7 ? `${gap.days_until_impact}d` :
                             `${gap.weeks_until_impact}w`}
                          </div>
                          <div className="text-xs text-gray-400">
                            {formatDate(gap.earliest_need_date)}
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleCreatePR(gap.boq_id)}
                            className="text-xs"
                            data-testid={`create-pr-${gap.boq_id}`}
                          >
                            <ShoppingCart className="w-3 h-3 mr-1" />
                            Create PR
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {loading && viewMode !== 'list' && (
        <div className="text-center py-8 text-gray-500">
          <RefreshCw className="w-8 h-8 mx-auto animate-spin mb-2" />
          Analyzing material gaps...
        </div>
      )}
    </div>
  );
}
